// GuestResolutionDialog.js
// Shown when a booking's mobile number already belongs to ANOTHER guest and the
// OCR-scanned guest does not match. We NEVER overwrite the scanned (OCR) data
// automatically — the receptionist explicitly chooses how to continue.
//
// Identity priority (mobile is NOT the primary identity):
//   Aadhaar > Passport > Driving Licence > Guest ID > Mobile > Email > Name+DOB
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  Card,
  Typography,
  Button,
  Chip,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PersonIcon from '@mui/icons-material/Person';
import HistoryIcon from '@mui/icons-material/History';
import MergeTypeIcon from '@mui/icons-material/MergeType';

const OCR_GREEN = '#16A34A';
const EXISTING_BLUE = '#2563eb';
const MERGE_AMBER = '#f59e0b';

// Mask an Aadhaar / ID number, keeping only the last 4 digits — "XXXX XXXX 4521".
export function maskAadhaar(num) {
  const digits = String(num || '').replace(/\s+/g, '');
  if (!digits) return '—';
  if (digits.length <= 4) return digits;
  const masked = 'X'.repeat(digits.length - 4) + digits.slice(-4);
  return masked.replace(/(.{4})/g, '$1 ').trim();
}

// One label/value row inside a comparison card.
const Field = ({ label, value }) => (
  <Box sx={{ mb: 1 }}>
    <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#0f172a', wordBreak: 'break-word' }}>
      {value || value === 0 ? value : '—'}
    </Typography>
  </Box>
);

export default function GuestResolutionDialog({
  open,
  onClose,
  ocrGuest = {},
  existingGuest = {},
  canMerge = false,
  onContinueOcr,
  onUseExisting,
  onMerge,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      fullScreen={fullScreen}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <WarningAmberIcon sx={{ fontSize: 34, color: MERGE_AMBER }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 20, color: '#0f172a', lineHeight: 1.2 }}>
              Guest Match Found
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#64748b', mt: 0.3 }}>
              This mobile number is already associated with another guest.
              Please choose how you want to continue.
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          {/* ── OCR Guest (green) ── */}
          <Grid item xs={12} md={6}>
            <Card
              variant='outlined'
              sx={{
                p: 2,
                height: '100%',
                border: `2px solid ${OCR_GREEN}`,
                borderRadius: 2,
                background: 'rgba(22,163,74,0.03)',
              }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
                <PersonIcon sx={{ color: OCR_GREEN, fontSize: 20 }} />
                <Typography sx={{ fontWeight: 800, color: OCR_GREEN, fontSize: 15 }}>OCR Guest</Typography>
                <Chip size='small' label='Scanned' sx={{ ml: 'auto', bgcolor: 'rgba(22,163,74,0.12)', color: OCR_GREEN, fontWeight: 700, height: 22 }} />
              </Box>
              <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0f172a', mb: 1.5 }}>
                {ocrGuest.name || '—'}
              </Typography>
              <Field label='Date of Birth' value={ocrGuest.dob} />
              <Field label='Aadhaar' value={maskAadhaar(ocrGuest.aadhaar)} />
              <Field label='Gender' value={ocrGuest.gender} />
              <Field label='Address' value={ocrGuest.address} />
            </Card>
          </Grid>

          {/* ── Existing Guest (blue) ── */}
          <Grid item xs={12} md={6}>
            <Card
              variant='outlined'
              sx={{
                p: 2,
                height: '100%',
                border: `2px solid ${EXISTING_BLUE}`,
                borderRadius: 2,
                background: 'rgba(37,99,235,0.03)',
              }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
                <HistoryIcon sx={{ color: EXISTING_BLUE, fontSize: 20 }} />
                <Typography sx={{ fontWeight: 800, color: EXISTING_BLUE, fontSize: 15 }}>Existing Guest</Typography>
                <Chip size='small' label='In database' sx={{ ml: 'auto', bgcolor: 'rgba(37,99,235,0.12)', color: EXISTING_BLUE, fontWeight: 700, height: 22 }} />
              </Box>
              <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0f172a', mb: 1.5 }}>
                {existingGuest.name || '—'}
              </Typography>
              <Field label='Mobile' value={existingGuest.mobile} />
              <Field label='Last Stay' value={existingGuest.lastStay} />
              <Field label='Bookings' value={existingGuest.bookings} />
              <Field label='Guest Since' value={existingGuest.guestSince} />
              <Field label='Guest ID' value={existingGuest.guestId} />
            </Card>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 1.5 },
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 1,
          '& > button': { width: { xs: '100%', sm: 'auto' }, ml: { xs: '0 !important', sm: undefined } },
        }}>
        {/* Merge — admin only, pushed to the left */}
        {canMerge && (
          <Button
            onClick={onMerge}
            startIcon={<MergeTypeIcon />}
            variant='outlined'
            sx={{
              mr: { sm: 'auto' },
              color: MERGE_AMBER,
              borderColor: MERGE_AMBER,
              fontWeight: 700,
              '&:hover': { borderColor: MERGE_AMBER, background: 'rgba(245,158,11,0.08)' },
            }}>
            Merge Guest Records
            <Chip size='small' label='Admin' sx={{ ml: 1, height: 18, fontSize: 10, fontWeight: 700, bgcolor: 'rgba(245,158,11,0.15)', color: MERGE_AMBER }} />
          </Button>
        )}

        {/* Secondary: Use Existing (blue) */}
        <Button
          onClick={onUseExisting}
          variant='outlined'
          sx={{
            color: EXISTING_BLUE,
            borderColor: EXISTING_BLUE,
            fontWeight: 700,
            '&:hover': { borderColor: EXISTING_BLUE, background: 'rgba(37,99,235,0.06)' },
          }}>
          Use Existing Guest
        </Button>

        {/* Primary: Continue with OCR (green) */}
        <Button
          onClick={onContinueOcr}
          variant='contained'
          sx={{
            bgcolor: OCR_GREEN,
            fontWeight: 700,
            boxShadow: 'none',
            '&:hover': { bgcolor: '#15803d', boxShadow: 'none' },
          }}>
          Continue with OCR Guest
        </Button>
      </DialogActions>
    </Dialog>
  );
}
